import mapboxgl from 'mapbox-gl';

class layerAndSourceObject {

    constructor(map, parent, groupId, sourceId) {
        this._map = map;
        this._parent = parent;
        this._groupId = groupId;
        this._sourceId = sourceId;

        /**
         * since we can not get all features from the map if we use clusters, we have to save them somewhere in case we need them
         * @type {Array}
         * @private
         */
        this._sourceFeatures = [];

        /**
         * tracks the first time a source was initialised / source data was added
         * @type {boolean}
         * @private
         */
        this._isSourceInitialised = false;
        /**
         * tracks every time a source was loaded, even if the source was only changed
         * @type {boolean}
         * @private
         */
        this._isSourceReady = false;
        this._onSourceReady = $.Deferred();

        // a simple flag to determine if source is currently loading. it is a simple check to prevent
        // that quick sequential calls of addSource or changeSource will mess everything up
        this._sourceIsLoading = false;

        //all layers and the source to track when base map style changes
        this._allLayerOptions = {};
        this._allSourceOptions = {};

        // the layer and source object can manage multiple layers at once. This way we can group together different
        // representations of a single data source (for example, tours and POIs)
        this._layerIds = [];

        this._filter = [];
    }

    /**
     * private functions
     */
    resolveSourceLoaded() {
        if (this._onSourceReady != null) {
            this._isSourceReady = true;
            this._onSourceReady.resolve();
        }
    }

    /**
     * end
     */

    /**
     * @region public functions
     */
    addLayer(layerDefinition) {
        if (!this._isSourceReady) {
            throw new Error(`Source not initialised yet, source name:${this._sourceId}`);
        }
        this._allLayerOptions[layerDefinition['id']] = layerDefinition;
        this._map.addLayer(layerDefinition);
        this._layerIds.push(layerDefinition['id']);
    }

    isSourceInitialized() {
        return this._isSourceInitialised;
    }

    addSource(sourceOptions) {
        var deferred = $.Deferred();
        if (this._sourceIsLoading) {
            deferred.reject();
            return deferred.promise();
        }
        this._allSourceOptions = sourceOptions;
        if (this.isSourceInitialized()) {
            // double check if a source was already loaded
            var existingSource = this._map.getSource(this._sourceId);
            if (!!existingSource) {
                //if the source exists, we change it
                return this.changeSource(sourceOptions);
            }
        }

        if (sourceOptions.data) {
            this._sourceFeatures = sourceOptions.data;
        }

        this._sourceIsLoading = true;
        if (sourceOptions.type == "userdefined") {
            sourceOptions.userFunction({
                'sourceId': this._sourceId,
                'source': sourceOptions,
                'layerAndSourceInfo': this
            }).then(() => {
                //once the map is idle we can resolve the source ready deferred
                map.once('idle', (ee) => {
                    this._isSourceReady = true;
                    this._onSourceReady.resolve();
                    this._sourceIsLoading = false;
                    deferred.resolve();
                });

            }, () => {
                this._sourceIsLoading = false;
                deferred.reject();
            });
        }
        else {
            var sourceExists = this._map.getSource(this._sourceId);
            if (!!sourceExists) {
                deferred.resolve();
                return deferred.promise();
            }
            this._map.addSource(this._sourceId, sourceOptions);
            this._sourceIsLoading = false;
            this._isSourceReady = true;
            this._isSourceInitialised = true;

            //once the map is idle we can resolve the source ready deferred
            this._map.once('idle', (ee) => {
                deferred.resolve();
                this._onSourceReady.resolve();
            });
        }
        return deferred.promise();
    }

    removeLayer(layerId) {
        this._map.removeLayer(layerId);
        this._layerIds = this._layerIds.filter(x => x != layerId);
        if (this._allLayerOptions.hasOwnProperty(layerId))
            delete this._allLayerOptions[layerId];
    }

    changeSource(sourceOptions) {

        var deferred = $.Deferred();

        this._allSourceOptions = sourceOptions;

        if (this._sourceIsLoading) {
            deferred.reject();
            return deferred.promise();
        }

        if (this._isSourceReady) {
            this._onSourceReady = $.Deferred();
            this._isSourceReady = false;
        }

        if (!this.isSourceInitialized()) {
            return this.addSource(sourceOptions);
        }

        if (sourceOptions.data) {
            this._sourceFeatures = sourceOptions.data;
        }

        let oldLayerDefinitions = [];

        let oldLayers = this._map.getStyle().layers;
        for (var i in this._layerIds) {
            let layerId = this._layerIds[i];
            let layerIndex = oldLayers.findIndex(l => l.id === layerId);
            let layerDef = oldLayers[layerIndex];
            let before = oldLayers[layerIndex + 1] && oldLayers[layerIndex + 1].id;
            oldLayerDefinitions.push({
                layerDef: layerDef,
                before: before
            });
            this._map.removeLayer(layerId);
        }
        this._map.removeSource(this._sourceId);

        if (sourceOptions.type == "userdefined") {
            sourceOptions.userFunction({
                'sourceId': this._sourceId,
                'source': sourceOptions,
                'layerAndSourceInfo': this
            }).then(() => {
                //once the map is idle we can resolve the source ready deferred
                this._map.once('idle', (ee) => {
                    deferred.resolve();
                    this._onSourceReady.resolve();
                });

            }, () => {
                deferred.reject();
            });
        }
        else {
            this._map.addSource(this._sourceId, sourceOptions);

            //once the map is idle we can resolve the source ready deferred
            map.once('idle', (ee) => {
                deferred.resolve();
                this._onSourceReady.resolve();
            });

        }
        deferred.done(() => {
            for (var i in oldLayerDefinitions) {
                let oldLayerDefinition = oldLayerDefinitions[i];
                if (!!oldLayerDefinition.before) {
                    this._map.addLayer(oldLayerDefinition.layerDef, oldLayerDefinition.before);
                }
                else {
                    this._map.addLayer(oldLayerDefinition.layerDef);
                }
            }
        });
        return deferred.promise();
    }

    changeSourceData(data) {
        if (!this.isSourceInitialized()) return;
        var source = this._map.getSource(this._sourceId);
        if (!source) return;

        if (this._allSourceOptions && this._allSourceOptions.data) {
            this._allSourceOptions.data = data;
        }

        if (this._isSourceReady) {
            this._isSourceReady = false;
            this._onSourceReady = $.Deferred();
        }
        source.setData(data);
    }

    getData() {
        return this._sourceFeatures;
    }

    sourceReady() {
        let source = this._map.getSource(this._sourceId);
        if (!!source && source._loaded) {
            return $.when(); //if already loaded we resolve it emediately
        }
        return this._onSourceReady.promise();
    }

    resetSourceReady() {
        if (!this._onSourceReady || this._onSourceReady.state() != "pending") {
            this._isSourceReady = false;
            this._onSourceReady = $.Deferred();
        }
        return this._onSourceReady.promise();
    }

    refreshSourceAndLayers() {
        if (this._allSourceOptions && this._sourceId) {
            if (!this._map.getSource(this._sourceId)) {
                this.addSource(this._allSourceOptions);
            }
        }
        if (!!this._allLayerOptions) {
            this._layerIds = [];
            _.forOwn(this._allLayerOptions, (option, key) => {
                let layerId = option.id;
                if (!!layerId) {
                    if (!this._map.getLayer(layerId)) {
                        this.addLayer(option);
                    }
                }
            });
        }
    }

    setFeatureState(ids, featureState) {
        if (!this._isSourceInitialised || !this._isSourceReady)
            return;
        ids = _.isArray(ids) ? ids : [ids];
        ids.map(id => {
            this._map.setFeatureState({
                source: this._sourceId,
                id: id
            }, featureState);
        });
    }

    /**
     * @endregion
     */
}

/**
 * @region private objects to track the layers and sources
 */
let _layerAndSourceMap = {};
let _map = null;
let _parent = null;
const layerIdPostfix = "_layer";
const sourceIdPostfix = "_source";
/**
 * @endregion
 */

/**
 * @region private factory functions
 */
let methods = {
    getIds: (id) => {
        let ids = {};
        let parts = id.split(".");
        ids.groupId = parts.shift();

        ids.hasSubgroup = parts.length > 0;
        ids.subgroupId = parts.length > 0 ? parts.join("_") : "default";
        ids.layerId = `${ids.groupId}_${ids.subgroupId}${layerIdPostfix}`;
        ids.sourceId = `${ids.groupId}${sourceIdPostfix}`;
        return ids;
    },
    foreachLayer: (id, callback) => {
        var deferred = $.Deferred();
        let ids = methods.getIds(id);
        let layerInfo = [];
        if (!ids.groupId) {
            deferred.reject();
            return deferred.promise();
        }
        if (!_layerAndSourceMap[ids.groupId]) {
            deferred.reject();
            return deferred.promise();
        }

        /**
         * if we have a subgroup we need to operate only on the sublayer
         */
        if (ids.hasSubgroup) {
            if (!_layerAndSourceMap[ids.groupId]._layerIds.find(id => id == ids.layerId)) {
                deferred.reject();
                return deferred.promise();
            }
            layerInfo.push(ids.layerId);
        }
        else {
            layerInfo = _layerAndSourceMap[ids.groupId]._layerIds;
        }
        var promises = [];
        layerInfo.forEach(layerId => {
            promises.push(callback(layerId, _layerAndSourceMap[ids.groupId]));
        });
        promises = promises.filter(x => x != null);
        if (promises.length > 0) {
            $.when.apply($, promises).then(() => {
                deferred.resolve();
            });
        }
        else {
            deferred.resolve();
        }
        return deferred.promise();
    },
    getLayerAndSourceGroup: (id) => {
        let ids = methods.getIds(id);
        if (!ids.groupId) {
            return;
        }
        if (!_layerAndSourceMap[ids.groupId])
            return;
        let layerAndSourceObj = _layerAndSourceMap[ids.groupId];
        return layerAndSourceObj;
    }
};
/**
 * @endregion
 */

/**
 * @region the exposed factory
 */
const layerFactory = {
    initialise: (map, parent) => {
        _map = map;
        _parent = parent;
    },
    hasLayer: (id) => {
        let ids = methods.getIds(id);
        const layer = _map.getLayer(ids.layerId);
        return layer !== undefined;
    },
    hasLayerAndSourceObject: (id) => {
        let ids = methods.getIds(id);
        return _layerAndSourceMap.hasOwnProperty(ids.groupId);
    },
    addLayer: (id, layerOptions) => {

        // first lets get the group and layer ids
        let ids = methods.getIds(id);
        let groupId = ids.groupId;
        let layerId = ids.layerId;
        let sourceId = ids.sourceId;

        if (!_layerAndSourceMap[groupId]) {
            _layerAndSourceMap[groupId] = new layerAndSourceObject(_map, _parent, groupId, sourceId);
        }

        layerOptions.id = layerId;
        layerOptions.source = sourceId;
        _layerAndSourceMap[groupId].addLayer(layerOptions);
    },
    addSource: (id, source) => {
        var layerAndSourceInfo = methods.getLayerAndSourceGroup(id);
        if (!layerAndSourceInfo) {
            let ids = methods.getIds(id);
            let groupId = ids.groupId;
            let sourceId = ids.sourceId;

            _layerAndSourceMap[groupId] = new layerAndSourceObject(_map, _parent, groupId, sourceId);
            layerAndSourceInfo = _layerAndSourceMap[groupId];
        }
        return layerAndSourceInfo.addSource(source);
    },
    changeSource: (id, source) => {
        var layerAndSourceGroup = methods.getLayerAndSourceGroup(id);
        if (!!layerAndSourceGroup) {
            layerAndSourceGroup.changeSource(source);
        }
    },
    changeSourceData: (id, geojsonData) => {
        var layerAndSourceGroup = methods.getLayerAndSourceGroup(id);
        if (!!layerAndSourceGroup) {
            layerAndSourceGroup.changeSourceData(geojsonData);
        }
    },
    hide: (id) => {
        methods.foreachLayer(id, (layerId, info) => {
            //@todo create a hide method inside layerAndSourceObject
            if (_map.getLayer(layerId))
                _map.setLayoutProperty(layerId, 'visibility', 'none');
            return null;
        });
    },
    show: (id) => {
        var layerAndSourceGroup = methods.getLayerAndSourceGroup(id);
        if (layerAndSourceGroup)
            layerAndSourceGroup.resetSourceReady();
        methods.foreachLayer(id, (layerId, info) => {
            //@todo create a show method inside layerAndSourceObject
            if (_map.getLayer(layerId))
                _map.setLayoutProperty(layerId, 'visibility', 'visible');
        });
    },
    remove: (id) => {
        var layerAndSourceGroup = methods.getLayerAndSourceGroup(id);
        if (!layerAndSourceGroup)
            return;
        var layerIds = layerAndSourceGroup._layerIds;
        layerIds.forEach(layerId => {
            layerAndSourceGroup.removeLayer(layerId);
        });
    },
    setFilter: (id, filter) => {
        var layerAndSourceGroup = methods.getLayerAndSourceGroup(id);
        var data = layerAndSourceGroup.getData();
        if (_.isFunction(filter)) {
            var features = [];
            if (data && data.features) {
                features = data.features;
            }
            var filteredFeatures = filter(features);
            layerFactory.changeSourceData(id, {
                type: "FeatureCollection",
                features: filteredFeatures
            });
        }
        else {
            layerFactory.changeSourceData(id, data);
            foreachLayer(id, (layerId, info) => {
                //@todo create setFilter function inside LayerAndSourceObject
                _map.setFilter(layerId, filter);
                return null;
            });
        }
    },
    clearFilter: (id) => {
        var layerAndSourceGroup = methods.getLayerAndSourceGroup(id);
        var data = layerAndSourceGroup.getData();
        layerFactory.changeSourceData(id, data);
        foreachLayer(id, (layerId, info) => {
            //@todo create setFilter function inside LayerAndSourceObject
            _map.setFilter(layerId, []);
            return null;
        });
    },
    foreachLayer: (id, callback) => {
        return methods.foreachLayer(id, (layerId, info) => {
            let layer = _map.getLayer(layerId);
            if (!!layer) {
                return callback(layerId, layer);
            }
            return null;
        });
    },
    getSource: id => {
        let layerAndSourceInfo = methods.getLayerAndSourceGroup(id);
        if (!layerAndSourceInfo)
            return;
        let source = _map.getSource(layerAndSourceInfo.sourceId);
        if (!!source) {
            return source;
        }
        return null;
    },
    onSourceReady: id => {
        let layerAndSourceInfo = methods.getLayerAndSourceGroup(id);
        if (!layerAndSourceInfo) {
            let response = $.Deferred();
            response.reject();
            return reject.promise();
        }
        return layerAndSourceInfo.sourceReady();
    },
    setFeatureState: (sourceObjectId, featureIds,state) => {
        let layerAndSourceInfo = methods.getLayerAndSourceGroup(sourceObjectId);
        if(!!layerAndSourceInfo)
            layerAndSourceInfo.setFeatureState(featureIds, state);
    },
    setLayoutProperty: (sourceObjectId, layers ,name, value) => {
        let layerAndSourceInfo = methods.getLayerAndSourceGroup(sourceObjectId);
        if(!!layerAndSourceInfo){
            if(_.isString(layers)){
                layers = [layers];
            }
            if(_.isArray(layers)){
                _.each(layers, layer => {
                    let ids = methods.getIds(layer);
                    let layerId = ids.layerId;
                    var mapLayer = _map.getLayer(layerId);
                    if(typeof mapLayer !== 'undefined')
                        _map.setLayoutProperty(layerId, name, value);
                });
            }
        }
    },
    setPaintProperty: (sourceObjectId, layers ,name, value) => {
        let layerAndSourceInfo = methods.getLayerAndSourceGroup(sourceObjectId);
        if(!!layerAndSourceInfo){
            if(_.isString(layers)){
                layers = [layers];
            }
            if(_.isArray(layers)){
                _.each(layers, layer => {
                    let ids = methods.getIds(layer);
                    let layerId = ids.layerId;
                    var mapLayer = _map.getLayer(layerId);
                    if(typeof mapLayer !== 'undefined')
                        _map.setPaintProperty(layerId, name, value);
                });
            }
        }
    },
    getBounds: id => {
        let layerAndSourceInfo = methods.getLayerAndSourceGroup(id);
        if (!layerAndSourceInfo)
            return null;
        let data = layerAndSourceInfo.getData();
        let features = !!data.features ? data.features : [data];
        if (!!features) {
            let bounds = new mapboxgl.LngLatBounds();
            features.map(feature => {
                let coordinates = [];
                if (!!feature._geometry) {
                    coordinates = feature._geometry.coordinates;
                }
                else {
                    coordinates = feature.geometry.coordinates;
                }
                if (_.isArray(coordinates[0])) // we have a tour
                {
                    coordinates.map(point => {
                        bounds.extend(point);
                    });
                }
                else {
                    bounds.extend(coordinates);
                }
            });
            return bounds;
        }
        return null;
    },
    getLayerAndSourceGroup: (id) => {
        return methods.getLayerAndSourceGroup(id);
    },
    getLayerIds: (id) => {
        var layerAndSourceGroup = methods.getLayerAndSourceGroup(id);
        if (layerAndSourceGroup)
            return layerAndSourceGroup._layerIds;
        return [];
    },
    getSourceId: (id) => {
        var layerAndSourceGroup = methods.getLayerAndSourceGroup(id);
        if (layerAndSourceGroup)
            return layerAndSourceGroup._sourceId;
        return null;
    },
    getAllFeatures: (id) => {
        var layerAndSourceGroup = methods.getLayerAndSourceGroup(id);
        if (layerAndSourceGroup)
            return layerAndSourceGroup.getData();
        return null;
    },
    getAllLayers: () => {
        return _layerAndSourceMap;
    },
    refresh: () => {
        _.forOwn(_layerAndSourceMap, (value, key) => {
            value.refreshSourceAndLayers();
        });
    },
    layerToFront(id){
        methods.foreachLayer(id, (layerId, info) => {
            var mapLayer = _map.getLayer(layerId);
            if(typeof mapLayer !== 'undefined')
                _map.moveLayer(layerId);
        });
    },
    on: (eventName, id, callback) => {
        methods.foreachLayer(id, (layerId, info) => {
            _map.on(eventName, layerId, (e) => {
                callback(e);
            });
        });
    }
};
export default layerFactory;
/**
 * @endregion
 */

